import { ForbiddenException, UnauthorizedException } from "@eisox/api";
import { cn, toast } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { z } from "@eisox/zod";

import type { SignInCredentials } from "../auth.type";
import { m } from "../paraglide/messages";

interface SignInFormProps extends Omit<React.ComponentProps<"form">, "onSubmit" | "children"> {
  onSubmit: (credentials: SignInCredentials) => Promise<void>;
  children?: React.ReactNode;
}

const signInErrorMessage = (caught: unknown): string => {
  if (caught instanceof UnauthorizedException) return m.error_unauthorized();
  if (caught instanceof ForbiddenException) return m.error_forbidden();
  return m.error_unknown();
};

const SignInForm: React.FC<SignInFormProps> = ({ onSubmit, children, ...props }) => {
  const form = useAppForm({
    defaultValues: { email: "", password: "" },
    validators: {
      onSubmit: z.object({
        email: z.email(m.email_invalid()),
        password: z.string().nonempty(m.password_required()),
      }),
    },
    onSubmit: async ({ value }) => {
      try {
        await onSubmit({ email: value.email, password: value.password });
      } catch (caught) {
        toast.error(signInErrorMessage(caught));
      }
    },
  });

  return (
    <form
      {...props}
      className={cn("gap-10 w-100 flex max-w-full flex-col items-center", props.className)}
      onSubmit={event => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <form.AppField name="email">
          {field => (
            <field.TextField
              classNames={{ root: "w-full", control: "w-full" }}
              label={m.email()}
              placeholder={m.email_placeholder()}
            />
          )}
        </form.AppField>
        <form.AppField name="password">
          {field => (
            <field.PasswordField
              classNames={{ root: "w-full", control: "w-full" }}
              label={m.password()}
              placeholder={m.password_placeholder()}
            />
          )}
        </form.AppField>
        <form.SubmitButton label={m.sign_in()} />
        {children}
      </form.AppForm>
    </form>
  );
};

export { SignInForm };
export type { SignInFormProps };
